import { useEffect, useState } from 'react';
import { api, ApiError, type SecurityView } from '../api';
import { PinPad } from '../components/PinPad';
import { Field, Header, Notice, Toggle, WalletId } from '../components/ui';
import { alertUser, haptic } from '../telegram';

const TRC20_RE = /^T[1-9A-HJ-NP-Za-km-z]{25,34}$/;

type PinStep = 'old' | 'new' | 'confirm';

export function SecurityPage() {
  const [view, setView] = useState<SecurityView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [bindOnly, setBindOnly] = useState(false);
  const [limit, setLimit] = useState('');
  const [pinFlow, setPinFlow] = useState<PinStep | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ oldPin?: string; pin?: string }>({});
  /** Which action the PIN pad is currently authorising. */
  const [confirming, setConfirming] = useState<'address' | 'limit' | null>(null);

  const load = (): void => {
    api
      .security()
      .then((data) => {
        setView(data);
        setAddress(data.bindAddress ?? '');
        setBindOnly(data.bindAddressOnly);
        setLimit(data.pinFreeLimit);
      })
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(load, []);

  const savePin = async (pin: string): Promise<void> => {
    try {
      await api.setPin({ pin, confirmPin: pin, ...(draft.oldPin ? { oldPin: draft.oldPin } : {}) });
      haptic('success');
      setPinFlow(null);
      setDraft({});
      alertUser('安全密码已设置');
      load();
    } catch (err) {
      const apiError = err as ApiError;
      haptic('error');
      setPinError(apiError.message);
      setPinFlow(apiError.code === 'PIN_WRONG' ? 'old' : 'new');
      setDraft({});
    }
  };

  const onPinSubmit = (value: string): void => {
    setPinError(null);
    if (confirming) {
      const action = confirming;
      setConfirming(null);
      setPinFlow(null);
      if (action === 'address') {
        api
          .bindAddress({ address: address.trim() || null, bindAddressOnly: bindOnly, pin: value })
          .then((data) => {
            haptic('success');
            setView(data);
            alertUser('提现地址设置已保存');
          })
          .catch((err: ApiError) => {
            haptic('error');
            alertUser(err.message);
          });
        return;
      }
      api
        .setPinFreeLimit({ limit, pin: value })
        .then((data) => {
          haptic('success');
          setView(data);
          alertUser('免密额度已更新');
        })
        .catch((err: ApiError) => {
          haptic('error');
          alertUser(err.message);
        });
      return;
    }

    if (pinFlow === 'old') {
      setDraft({ oldPin: value });
      setPinFlow('new');
      return;
    }
    if (pinFlow === 'new') {
      setDraft((current) => ({ ...current, pin: value }));
      setPinFlow('confirm');
      return;
    }
    if (pinFlow === 'confirm') {
      if (draft.pin !== value) {
        setPinError('两次输入的安全密码不一致');
        setPinFlow('new');
        setDraft((current) => ({ oldPin: current.oldPin }));
        return;
      }
      void savePin(value);
    }
  };

  if (error) {
    return (
      <div className="app">
        <Header title="安全设置" />
        <div className="body">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="app">
        <Header title="安全设置" />
        <div className="loading">加载中…</div>
      </div>
    );
  }

  const pinTitle =
    pinFlow === 'old' ? '请输入原安全密码' : pinFlow === 'confirm' ? '请再次输入新密码' : '请输入新安全密码';

  return (
    <div className="app">
      <Header title="安全设置" />
      <WalletId id={view.walletId} pinReady={view.hasPin} />
      <div className="body">
        <button
          type="button"
          className="security-menu"
          onClick={() => {
            setPinError(null);
            setDraft({});
            setPinFlow(view.hasPin ? 'old' : 'new');
          }}
        >
          <span>设置安全密码</span><span>{view.hasPin ? '已设置' : '未设置'} ›</span>
        </button>

        <details className="security-group"><summary><span>提现地址绑定（账户保护）</span><span>{view.bindAddress ? '已绑定' : '未设置'} ›</span></summary><div>
        <Field
          label="提现地址（USDT-TRC20）"
          hint={<span>绑定后可开启「提现走绑定地址」，只允许提现到该地址</span>}
        >
          <input
            className="mono"
            placeholder="请输入您的提现地址"
            value={address}
            spellCheck={false}
            autoCapitalize="none"
            onChange={(event) => setAddress(event.target.value)}
          />
        </Field>

        <Toggle
          label="提现走绑定地址"
          hint="默认关闭"
          value={bindOnly}
          onChange={(next) => setBindOnly(next)}
        />

        <button
          type="button"
          className="ghost"
          disabled={address.trim() !== '' && !TRC20_RE.test(address.trim())}
          onClick={() => {
            if (!view.hasPin) {
              alertUser('请先设置 6 位安全密码');
              return;
            }
            setPinError(null);
            setConfirming('address');
            setPinFlow('old');
          }}
        >
          保存地址设置
        </button>

        </div></details>
        <details className="security-group"><summary><span>权限设置（免密额度）</span><span>{view.pinFreeLimit} USDT ›</span></summary><div>
        <Field
          label="免密额度（USDT）"
          hint={<span>换汇金额低于该额度时免输密码；提现始终需要密码</span>}
        >
          <input
            inputMode="decimal"
            value={limit}
            onChange={(event) => setLimit(event.target.value.replace(/[^\d.]/g, ''))}
          />
          <span className="suffix">USDT</span>
        </Field>

        <button
          type="button"
          className="ghost"
          onClick={() => {
            if (!view.hasPin) {
              alertUser('请先设置 6 位安全密码');
              return;
            }
            setPinError(null);
            setConfirming('limit');
            setPinFlow('old');
          }}
        >
          保存免密额度
        </button>

        </div></details>
        <Notice>{view.notice}</Notice>
      </div>

      {pinFlow ? (
        <PinPad
          title={confirming ? '请输入安全密码' : pinTitle}
          error={pinError}
          onSubmit={onPinSubmit}
          onCancel={() => {
            setPinFlow(null);
            setPinError(null);
            setConfirming(null);
            setDraft({});
          }}
        />
      ) : null}
    </div>
  );
}
